import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Avatar, CircularProgress,
  Snackbar, Alert, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, alpha,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Block as SuspendIcon, DeleteOutline as DeleteIcon,
  MoreVert as MoreIcon, Refresh as RefreshIcon, AdminPanelSettings as AdminIcon,
  People as PeopleIcon, PushPin as PinIcon, ScheduleSend as ScheduleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios, { API } from '../api';
import { designTokens as t } from '../theme';

const STATUS = {
  active:    { label: 'Actif',     color: t.success, bg: alpha(t.success, 0.14), fg: '#86efac' },
  pending:   { label: 'En attente', color: t.warning, bg: alpha(t.warning, 0.14), fg: '#fcd34d' },
  suspended: { label: 'Suspendu',  color: t.error,   bg: alpha(t.error, 0.14),   fg: '#fca5a5' },
  expired:   { label: 'Expiré',    color: t.mutedFaint, bg: alpha(t.mutedFaint, 0.14), fg: t.muted },
};

function fmtUntil(ts) {
  if (!ts) return 'illimité';
  const d = new Date(ts * 1000);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Timestamps venant du backend (Go time.Time / RFC3339). Le "zéro" Go
// (0001-01-01) est traité comme "jamais".
function fmtDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d) || d.getFullYear() < 2000) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState({ anchor: null, user: null });
  const [approveDlg, setApproveDlg] = useState({ open: false, user: null, days: '' });
  const [accountsDlg, setAccountsDlg] = useState({ open: false, user: null, loading: false, accounts: [] });
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/users`);
      setUsers(data.users || []);
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const approve = async (user, days) => {
    try {
      await axios.post(`${API}/api/admin/users/${user.id}/approve`, { days: Number(days) || 0 });
      notify(`Accès accordé à ${user.email}`);
      fetchUsers();
    } catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };
  const suspend = async (user) => {
    try { await axios.post(`${API}/api/admin/users/${user.id}/suspend`); notify(`${user.email} suspendu`); fetchUsers(); }
    catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };
  const remove = async (user) => {
    if (!window.confirm(`Supprimer définitivement ${user.email} et toutes ses données ?`)) return;
    try { await axios.delete(`${API}/api/admin/users/${user.id}`); notify(`${user.email} supprimé`); fetchUsers(); }
    catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const viewAccounts = async (user) => {
    if (!user) return;
    setAccountsDlg({ open: true, user, loading: true, accounts: [] });
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${user.id}/accounts`);
      setAccountsDlg({ open: true, user, loading: false, accounts: data.accounts || [] });
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement des comptes', 'error');
      setAccountsDlg({ open: false, user: null, loading: false, accounts: [] });
    }
  };

  const closeMenu = () => setMenu({ anchor: null, user: null });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}><CircularProgress /></Box>;
  }

  const pendingCount = users.filter((u) => u.status === 'pending').length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon sx={{ color: t.primary }} />
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Administration</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.88rem', color: t.muted }}>
            {users.length} utilisateur(s){pendingCount > 0 ? ` · ${pendingCount} en attente de validation` : ''}
          </Typography>
        </Box>
        <IconButton onClick={fetchUsers} sx={{ border: `1px solid ${t.border}`, bgcolor: t.card }}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, overflow: 'hidden' }}>
        {/* header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${t.border}`, bgcolor: t.background }}>
          {['Utilisateur', 'Statut', 'Abonnement', 'Comptes', ''].map((h, i) => (
            <Typography key={i} sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.mutedFaint }}>{h}</Typography>
          ))}
        </Box>
        {users.map((u) => {
          const st = STATUS[u.status] || STATUS.pending;
          return (
            <Box key={u.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px', gap: 1, px: 2, py: 1.5, alignItems: 'center', borderBottom: `1px solid ${alpha(t.border, 0.5)}`, '&:hover': { bgcolor: alpha('#fff', 0.02) } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', fontSize: '0.85rem', fontWeight: 600 }}>
                  {(u.email || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</Typography>
                  {u.role === 'admin' && <Chip size="small" label="admin" sx={{ height: 16, fontSize: '0.6rem', bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe' }} />}
                </Box>
              </Box>
              <Box><Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, border: `1px solid ${alpha(st.color, 0.3)}`, fontWeight: 600 }} /></Box>
              <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>{fmtUntil(u.access_until)}</Typography>
              {u.account_count > 0 ? (
                <Box onClick={() => viewAccounts(u)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: '#c7d2fe', width: 'fit-content', '&:hover': { textDecoration: 'underline' } }}>
                  <PeopleIcon sx={{ fontSize: 15 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{u.account_count}</Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.82rem', color: t.mutedFaint }}>0</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                {u.status !== 'active' && (
                  <Button size="small" variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 16 }} />}
                    onClick={() => approve(u, 0)} sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }}>
                    Activer
                  </Button>
                )}
                {u.role !== 'admin' && (
                  <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, user: u })}><MoreIcon sx={{ fontSize: 18 }} /></IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { viewAccounts(menu.user); closeMenu(); }}>
          <PeopleIcon sx={{ fontSize: 18, mr: 1, color: t.primary }} /> Voir les comptes
        </MenuItem>
        <MenuItem onClick={() => { setApproveDlg({ open: true, user: menu.user, days: '30' }); closeMenu(); }}>
          <ApproveIcon sx={{ fontSize: 18, mr: 1, color: t.success }} /> Activer avec durée…
        </MenuItem>
        <MenuItem onClick={() => { suspend(menu.user); closeMenu(); }}>
          <SuspendIcon sx={{ fontSize: 18, mr: 1, color: t.warning }} /> Suspendre
        </MenuItem>
        <MenuItem onClick={() => { remove(menu.user); closeMenu(); }} sx={{ color: t.error }}>
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Supprimer
        </MenuItem>
      </Menu>

      <Dialog open={approveDlg.open} onClose={() => setApproveDlg({ open: false, user: null, days: '' })}>
        <DialogTitle>Accorder un abonnement</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: t.muted, mb: 2 }}>
            Durée d'accès pour <b>{approveDlg.user?.email}</b>. Laisse vide ou 0 pour un accès <b>illimité</b>.
          </Typography>
          <TextField autoFocus fullWidth type="number" label="Durée (jours)" value={approveDlg.days}
            onChange={(e) => setApproveDlg((p) => ({ ...p, days: e.target.value }))} placeholder="30" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDlg({ open: false, user: null, days: '' })}>Annuler</Button>
          <Button variant="contained" onClick={() => { approve(approveDlg.user, approveDlg.days); setApproveDlg({ open: false, user: null, days: '' }); }}>
            Accorder l'accès
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accountsDlg.open} onClose={() => setAccountsDlg({ open: false, user: null, loading: false, accounts: [] })} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ color: t.primary }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>Comptes de {accountsDlg.user?.email}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>
              {accountsDlg.loading ? 'Chargement…' : `${accountsDlg.accounts.length} compte(s) Threads`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: t.border }}>
          {accountsDlg.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>
          ) : accountsDlg.accounts.length === 0 ? (
            <Typography sx={{ color: t.muted, textAlign: 'center', py: 4 }}>Aucun compte ajouté par cet utilisateur.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {accountsDlg.accounts.map((a) => {
                const suspended = a.suspended || a.status === 'suspended';
                return (
                  <Box key={a.name} sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 1.5, bgcolor: t.background }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', fontSize: '0.9rem', fontWeight: 600 }}>
                        <PersonIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.username ? `@${a.username}` : a.name}
                          </Typography>
                          {suspended && <Chip size="small" label="suspendu" sx={{ height: 18, fontSize: '0.62rem', bgcolor: alpha(t.error, 0.14), color: '#fca5a5' }} />}
                          {a.sticky && (
                            <Chip size="small" icon={<PinIcon sx={{ fontSize: 12, color: '#fff !important' }} />} label="STICKY"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#ec4899', color: '#fff' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.full_name ? `${a.full_name} · ` : ''}{a.name}{a.profile ? ` · groupe ${a.profile}` : ''}{a.has_proxy ? ' · proxy' : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1.25 }}>
                      {[
                        { label: 'Followers', value: a.followers != null ? Number(a.followers).toLocaleString('fr-FR') : '—' },
                        { label: 'En attente', value: a.pending || 0 },
                        { label: 'Publiés', value: a.published || 0 },
                        { label: 'Dernière activité', value: fmtDateTime(a.last_used) },
                      ].map((s, i) => (
                        <Box key={i} sx={{ textAlign: 'center', py: 0.75, borderRadius: 1.5, bgcolor: t.card, border: `1px solid ${alpha(t.border, 0.6)}` }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: t.foreground }}>{s.value}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{s.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                    {a.next_post && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#86efac' }}>
                        <ScheduleIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.74rem' }}>Prochain post : {fmtDateTime(a.next_post)}</Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountsDlg({ open: false, user: null, loading: false, accounts: [] })}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
